import React, { useEffect, useState } from 'react';
import { contactAPI } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Mail, MailOpen, Trash2, Eye, X } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

export const MessagesAdmin = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [viewMessage, setViewMessage] = useState(null);

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    try {
      const response = await contactAPI.getMessages();
      setMessages(response.data);
    } catch (error) {
      toast.error('Nepodařilo se načíst zprávy');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await contactAPI.markRead(id);
      setMessages(messages.map(m => m.id === id ? { ...m, is_read: true } : m));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await contactAPI.deleteMessage(deleteId);
      setMessages(messages.filter(m => m.id !== deleteId));
      toast.success('Zpráva byla smazána');
    } catch (error) {
      toast.error('Nepodařilo se smazat zprávu');
    } finally {
      setDeleteId(null);
    }
  };

  const openMessage = async (message) => {
    setViewMessage(message);
    if (!message.is_read) {
      await handleMarkRead(message.id);
    }
  };

  const unreadCount = messages.filter(m => !m.is_read).length;

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#2C3E50]">Zprávy</h1>
          <p className="text-[#64748B]">
            {unreadCount > 0 ? `${unreadCount} nepřečtených zpráv` : 'Všechny zprávy přečteny'}
          </p>
        </div>
      </div>

      {messages.length > 0 ? (
        <div className="space-y-3">
          {messages.map((message) => (
            <div 
              key={message.id}
              className={`bg-white rounded-2xl border p-5 cursor-pointer hover:shadow-md transition-shadow ${
                message.is_read ? 'border-slate-100' : 'border-[#4ECDC4] bg-[#F7FFFE]'
              }`}
              onClick={() => openMessage(message)}
              data-testid={`message-${message.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4 flex-1">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    message.is_read ? 'bg-slate-100' : 'bg-[#4ECDC4]'
                  }`}>
                    {message.is_read ? (
                      <MailOpen size={18} className="text-[#64748B]" />
                    ) : (
                      <Mail size={18} className="text-white" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`font-medium ${message.is_read ? 'text-[#64748B]' : 'text-[#2C3E50]'}`}>
                        {message.name}
                      </span>
                      {!message.is_read && (
                        <span className="px-2 py-0.5 rounded-full bg-[#4ECDC4] text-white text-xs">
                          Nová
                        </span>
                      )}
                    </div>
                    <div className={`text-sm ${message.is_read ? 'text-[#94A3B8]' : 'text-[#2C3E50]'} truncate`}>
                      {message.subject}
                    </div>
                    <div className="text-xs text-[#64748B] mt-1">{formatDate(message.created_at)}</div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteId(message.id);
                  }}
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-20 bg-white rounded-2xl border border-slate-100">
          <Mail size={48} className="mx-auto text-[#CBD5E1] mb-4" />
          <div className="text-[#64748B]">Žádné zprávy</div>
        </div>
      )}

      {/* View Message Dialog */}
      <Dialog open={!!viewMessage} onOpenChange={() => setViewMessage(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{viewMessage?.subject}</DialogTitle>
          </DialogHeader>
          {viewMessage && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-[#64748B]">Od</div>
                  <div className="font-medium">{viewMessage.name}</div>
                </div>
                <div>
                  <div className="text-[#64748B]">Email</div>
                  <a href={`mailto:${viewMessage.email}`} className="font-medium text-[#4ECDC4]">
                    {viewMessage.email}
                  </a>
                </div>
                {viewMessage.phone && (
                  <div>
                    <div className="text-[#64748B]">Telefon</div>
                    <a href={`tel:${viewMessage.phone}`} className="font-medium">
                      {viewMessage.phone}
                    </a>
                  </div>
                )}
                <div>
                  <div className="text-[#64748B]">Datum</div>
                  <div className="font-medium">{formatDate(viewMessage.created_at)}</div>
                </div>
              </div>
              
              <div className="border-t border-slate-100 pt-4">
                <div className="text-[#64748B] text-sm mb-2">Zpráva</div>
                <div className="text-[#2C3E50] whitespace-pre-wrap">{viewMessage.message}</div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <a href={`mailto:${viewMessage.email}?subject=Re: ${viewMessage.subject}`} className="flex-1">
                  <Button className="w-full rounded-full bg-[#4ECDC4] hover:bg-[#3DBDB5]">
                    Odpovědět
                  </Button>
                </a>
                <Button
                  variant="outline"
                  className="rounded-full text-red-600 hover:bg-red-50"
                  onClick={() => {
                    setViewMessage(null);
                    setDeleteId(viewMessage.id);
                  }}
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Smazat zprávu?</AlertDialogTitle>
            <AlertDialogDescription>Tato akce je nevratná.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Zrušit</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Smazat</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MessagesAdmin;
